// @ts-nocheck
/*
 * Port of views/dashboard/agent-break-exceeded/agent_break_exceeded_list.js.
 * API: API_AGENT_BREAK_EXCEEDED_STATS (?start_date=&end_date=).
 * Default range month-start→today, 180-day cap, sort by removed_at desc,
 * apex pie chart when exceeded>0.
 * Column count preserved: 7 (Agent ID, Agent Name, Break Title,
 * Allowed Duration, Started At, Removed At, Exceeded Time).
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { BarChart3, Clock, RefreshCw } from 'lucide-react';
import Chart from 'react-apexcharts';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall } from '@/service/httpCall';
import { API_AGENT_BREAK_EXCEEDED_STATS } from '@/service/url';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/data-table/DataTable';
import { WARNING_COLOR, PRIMARY_COLOR } from '../chart-palette';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

const MAX_FILTER_DAYS = 180;

const normalizeRows = (resp) => {
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.rows)) return resp.rows;
  return [];
};

const safeDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const formatISTDateTime = (value) => {
  const date = safeDate(value);
  if (!date) return '--';
  return date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
};

const formatDateForCell = (value) => {
  const date = safeDate(value);
  if (!date) return { date: '--', time: '' };
  return {
    date: date.toLocaleDateString('en-GB', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' }),
    time: date.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit' }),
  };
};

const formatDuration = (seconds) => {
  const s = Math.max(0, Number(seconds) || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rem = s % 60;
  const pad = (v) => String(v).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(rem)}`;
};

const getInitials = (name) => {
  const t = String(name || '').trim().split(' ').filter(Boolean);
  if (!t.length) return 'NA';
  if (t.length === 1) return t[0].slice(0, 2).toUpperCase();
  return `${t[0][0]}${t[1][0]}`.toUpperCase();
};

const startOfDay = (value) => {
  let d;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, day] = value.split('-').map((v) => parseInt(v, 10));
    d = new Date(y, m - 1, day);
  } else d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

const fmtInput = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const toDateInputValue = (v) => (v ? fmtInput(startOfDay(v)) : '');

const getDefaultDateRange = () => {
  const today = startOfDay(new Date());
  return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: today };
};

const getInclusiveDays = (from, to) => {
  const s = startOfDay(from).getTime();
  const e = startOfDay(to).getTime();
  return Math.floor((e - s) / (1000 * 60 * 60 * 24)) + 1;
};

const addDays = (d, n) => {
  const x = startOfDay(d);
  x.setDate(x.getDate() + n);
  return x;
};

export default function AgentBreakExceededList() {
  const defaultRange = getDefaultDateRange();
  const maxSelectableDate = startOfDay(new Date());
  const minSelectableDate = addDays(maxSelectableDate, -(MAX_FILTER_DAYS - 1));

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [fromDateValue, setFromDateValue] = useState(defaultRange.from);
  const [toDateValue, setToDateValue] = useState(defaultRange.to);

  const fetchExceededStats = ({ fromDate, toDate }) => {
    let isMounted = true;
    setLoading(true);
    const query = `?start_date=${encodeURIComponent(fromDate)}&end_date=${encodeURIComponent(toDate)}`;
    getApiCall(`${API_AGENT_BREAK_EXCEEDED_STATS}${query}`)
      .then((resp) => { if (isMounted) setRows(normalizeRows(resp)); })
      .catch(() => { if (isMounted) setRows([]); })
      .finally(() => { if (isMounted) setLoading(false); });
    return () => { isMounted = false; };
  };

  useEffect(() => {
    const cleanup = fetchExceededStats({
      fromDate: toDateInputValue(defaultRange.from),
      toDate: toDateInputValue(defaultRange.to),
    });
    return cleanup;

  }, []);

  const tableRows = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    return list
      .map((r, idx) => {
        const startedAt = safeDate(r?.start_at);
        const removedAt = safeDate(r?.end_at || r?.removed_at);
        return {
          key: r?.id || idx + 1,
          id: r?.id,
          agent_id: r?.user?.agent?.id || '--',
          agent_name: r?.user?.agent?.agent_name || '--',
          break_title: r?.break?.title || '--',
          allowed_duration: Number(r?.break?.duration) || 0,
          started_at_parts: formatDateForCell(startedAt),
          removed_at_parts: formatDateForCell(removedAt),
          exceeded_time: formatDuration(r?.exceeded_seconds),
          _startedTs: startedAt ? startedAt.getTime() : 0,
          _removedTs: removedAt ? removedAt.getTime() : 0,
        };
      })
      .sort((a, b) => (b._removedTs || 0) - (a._removedTs || 0));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    return tableRows.filter((r) => {
      if (!q) return true;
      return (
        String(r?.agent_name || '').toLowerCase().includes(q) ||
        String(r?.agent_id || '').toLowerCase().includes(q) ||
        String(r?.break_title || '').toLowerCase().includes(q)
      );
    });
  }, [searchValue, tableRows]);

  const chartStats = useMemo(() => {
    const totalExceeded = filteredRows.filter((item) => item.exceeded_time !== '00:00:00').length;
    return { totalExceeded, totalBreaksTaken: filteredRows.length };
  }, [filteredRows]);

  const pieOptions = {
    chart: { type: 'pie', toolbar: { show: false } },
    labels: ['Exceeded Breaks', 'Breaks Taken'],
    legend: { position: 'bottom' },
    dataLabels: { enabled: true },
    stroke: { width: 1 },
    colors: [WARNING_COLOR, PRIMARY_COLOR],
  };
  const pieSeries = [chartStats.totalExceeded, chartStats.totalBreaksTaken - chartStats.totalExceeded];

  const validateAndFetch = () => {
    if (!fromDateValue || !toDateValue) { toast.error('Please select both start and end date.'); return; }
    const s = startOfDay(fromDateValue);
    const e = startOfDay(toDateValue);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) { toast.error('Invalid date selected.'); return; }
    if (s > e) { toast.error('Start date cannot be after end date.'); return; }
    if (getInclusiveDays(s, e) > MAX_FILTER_DAYS) {
      toast.error(`Date range too large. Maximum allowed is ${MAX_FILTER_DAYS} calendar days (inclusive).`);
      return;
    }
    fetchExceededStats({ fromDate: toDateInputValue(fromDateValue), toDate: toDateInputValue(toDateValue) });
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      { accessorKey: 'agent_id', header: 'Agent ID' },
      {
        accessorKey: 'agent_name',
        header: 'Agent Name',
        cell: ({ row }) => (
          <div className="flex items-center gap-2.5">
            <div
              className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #8b80f9 0%, #5f56da 100%)' }}
            >
              {getInitials(row.original.agent_name)}
            </div>
            <span>{row.original.agent_name}</span>
          </div>
        ),
      },
      { accessorKey: 'break_title', header: 'Break Title' },
      {
        accessorKey: 'allowed_duration',
        header: 'Allowed Duration',
        cell: ({ row }) => (
          <span
            className="rounded-full border px-2.5 py-1 text-xs font-semibold"
            style={{ color: '#1c8b55', borderColor: '#e6f2ff' }}
          >
            {row.original.allowed_duration} min
          </span>
        ),
      },
      {
        id: 'started_at',
        header: 'Started At',
        cell: ({ row }) => (
          <div>
            <div className="font-semibold">{row.original.started_at_parts.date}</div>
            {row.original.started_at_parts.time && (
              <div className="mt-0.5 text-xs text-muted-foreground">{row.original.started_at_parts.time}</div>
            )}
          </div>
        ),
      },
      {
        id: 'removed_at',
        header: 'Removed At',
        cell: ({ row }) => (
          <div>
            <div className="font-semibold">{row.original.removed_at_parts.date}</div>
            {row.original.removed_at_parts.time && (
              <div className="mt-0.5 text-xs text-muted-foreground">{row.original.removed_at_parts.time}</div>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'exceeded_time',
        header: 'Exceeded Time',
        cell: ({ row }) => (
          <span
            className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold"
            style={{ color: '#d3304a', borderColor: '#e6f2ff' }}
          >
            <Clock className="h-3 w-3" />
            {row.original.exceeded_time}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-bold text-foreground">
          <BarChart3 className="h-4 w-4 text-primary" />
          Agent Break Stats
        </div>
        <button
          type="button"
          onClick={validateAndFetch}
          className="flex h-7 w-7 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          title="Refresh Agent Break Stats"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-[minmax(200px,1fr)_auto_auto_auto_auto]">
        <Input
          placeholder="Search by agent or break..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="h-9"
        />
        <CalendarDateInput
          value={toDateInputValue(fromDateValue)}
          onChange={(e) => setFromDateValue(e ? startOfDay(e) : null)}
          min={toDateInputValue(minSelectableDate)}
          max={toDateInputValue(toDateValue || defaultRange.to)}
          className="h-9 w-40"
        />
        <span className="flex items-center text-xs font-semibold text-muted-foreground">to</span>
        <CalendarDateInput
          value={toDateInputValue(toDateValue)}
          onChange={(e) => setToDateValue(e ? startOfDay(e) : null)}
          min={toDateInputValue(fromDateValue || minSelectableDate)}
          max={toDateInputValue(maxSelectableDate)}
          className="h-9 w-40"
        />
        <Button size="sm" onClick={validateAndFetch}>Filter</Button>
      </div>

      {chartStats.totalExceeded > 0 && (
        <div className="mb-4 shadow-neu-raised rounded-lg bg-card p-4 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
          <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-[1fr_2fr]">
            <div><Chart options={pieOptions} series={pieSeries} type="pie" height={280} /></div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <div className="text-xs text-muted-foreground">Total Exceeded Breaks</div>
                <div className="text-3xl font-bold" style={{ color: '#ef4444' }}>{chartStats.totalExceeded}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Total Breaks Taken</div>
                <div className="text-3xl font-bold" style={{ color: '#3b82f6' }}>{chartStats.totalBreaksTaken}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <DataTable
        tableId="dashboard.agent-break-exceeded"
        columns={columns}
        data={filteredRows}
        isLoading={loading}
        emptyTitle="No exceeded breaks"
        emptyDescription="No exceeded breaks found for the current filter."
        pageSize={10}
        exportFilename="agent-break-exceeded.csv"
      />
    </>
  );
}
